import { useEffect, useState } from 'react';
import { agentClasses, fetchAgents } from '../api/agents';
import { useAuth } from '../auth/AuthContext';
import {
  archiveMemoryEntry,
  fetchMemoryEntries,
  fetchMemoryStats,
  purgeMemoryEntry,
  updateMemoryEntry,
  searchMemory,
  type MemoryEntry,
  type MemoryStats,
} from '../api/memory';
import './MemoryPage.css';

// The agent id is asked for, not written down.
//
// This line was `'argv'`, was corrected to `'assistant'` on 2026-09-06,
// and both were wrong on `wingd`, whose classes are `coordinator` and
// `technical-support`. Three spellings of one defect, each looking like
// a repair: a literal agent id in a frontend that serves every instance.
// `/api/memory/{id}` answers `404 Agent class '<id>' not found.`, and
// `load()`'s `Promise.all` rejects on the first of the three calls, so
// the page shows an error banner rather than rendering empty — the stats
// call is wrapped in `.catch(() => null)` and swallows its own 404
// silently, which is why only one of the three was ever visible.
//
// ADR 030 D1: an instance differs by name, configuration and what it
// enables. Anything naming an agent has to ask the runtime.
function formatRelativeTime(ms: number): string {
  if (!ms) return '—';
  const diff = Date.now() - ms;
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  return new Date(ms).toISOString().slice(0, 10);
}

export function MemoryPage() {
  const { user } = useAuth();
  // Purging is admin-only server-side; offering it to anyone else would
  // only produce a 403.
  const canPurge = user?.role === 'admin';
  const [agents, setAgents] = useState<string[] | null>(null);
  const [agentId, setAgentId] = useState<string | null>(null);
  const [entries, setEntries] = useState<MemoryEntry[]>([]);
  const [stats, setStats] = useState<MemoryStats | null>(null);
  const [query, setQuery] = useState('');
  const [submittedQuery, setSubmittedQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Which agents exist is the instance's answer, asked once.
  useEffect(() => {
    let live = true;
    fetchAgents()
      .then((rows) => {
        if (!live) return;
        const classes = agentClasses(rows);
        setAgents(classes);
        // The first the runtime reports, which is its declaration order.
        // No preference is encoded here: a chooser that guessed which
        // agent mattered is how this page came to name one in source.
        setAgentId((current) => current ?? classes[0] ?? null);
        if (classes.length === 0) setLoading(false);
      })
      .catch((err: unknown) => {
        if (!live) return;
        setError(err instanceof Error ? err.message : String(err));
        setLoading(false);
      });
    return () => {
      live = false;
    };
  }, []);

  const load = (q: string) => {
    if (!agentId) return;
    setLoading(true);
    setError(null);
    const promise = q
      ? searchMemory(agentId, q, 100)
      : fetchMemoryEntries(agentId, { limit: 200 });
    Promise.all([promise, fetchMemoryStats(agentId).catch(() => null)])
      .then(([items, s]) => {
        setEntries(items);
        setStats(s);
      })
      .catch((err) => setError(err instanceof Error ? err.message : String(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(submittedQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittedQuery, agentId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmittedQuery(query.trim());
  };

  const handleClear = () => {
    setQuery('');
    setSubmittedQuery('');
  };

  return (
    <div className="memory-page">
      <header className="memory-page__header">
        <div>
          <div className="memory-page__title">Memory</div>
          <div className="memory-page__subtitle">
            {stats
              ? `${stats.total_entries} entries · ${stats.short_term_count} short-term · ${stats.long_term_count} long-term · ${stats.pinned_count} pinned`
              : `${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}`}
          </div>
        </div>
        {agents !== null && agents.length > 1 && (
          <label className="memory-page__agent">
            <span className="memory-page__agent-label">agent</span>
            <select
              value={agentId ?? ''}
              onChange={(e) => setAgentId(e.target.value)}
            >
              {agents.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </label>
        )}
        {agents !== null && agents.length === 1 && (
          <span className="memory-page__agent-single">{agents[0]}</span>
        )}
        <button
          type="button"
          className="memory-page__refresh"
          onClick={() => load(submittedQuery)}
          disabled={loading}
        >
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </header>

      <form className="memory-page__search" onSubmit={handleSubmit}>
        <input
          type="text"
          placeholder="Search memory (FTS)..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="memory-page__search-input"
        />
        <button type="submit" className="memory-page__search-submit">
          Search
        </button>
        {submittedQuery && (
          <button
            type="button"
            className="memory-page__search-clear"
            onClick={handleClear}
          >
            Clear
          </button>
        )}
      </form>

      {error && <div className="memory-page__error">{error}</div>}

      <div className="memory-page__list">
        {loading && entries.length === 0 && (
          <div className="memory-page__status">Loading...</div>
        )}
        {!loading && agents !== null && agents.length === 0 && (
          <div className="memory-page__status">
            This instance is running no agents, so there is no memory to
            show. Memory is agent-scoped (ADR 024).
          </div>
        )}
        {!loading && entries.length === 0 && agents !== null && agents.length > 0 && (
          <div className="memory-page__status">
            {submittedQuery
              ? `No matches for "${submittedQuery}" in ${agentId}'s memory.`
              : `${agentId} has no memory entries yet.`}
          </div>
        )}
        {agentId &&
          entries.map((entry) => (
            <EntryCard
              key={entry.key}
              agentId={agentId}
              entry={entry}
              canPurge={canPurge}
              onChanged={() => load(submittedQuery)}
            />
          ))}
      </div>
    </div>
  );
}

function EntryCard({
  agentId,
  entry,
  canPurge,
  onChanged,
}: {
  agentId: string;
  entry: MemoryEntry;
  canPurge: boolean;
  onChanged: () => void;
}) {
  const importance = entry.stated_importance;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(entry.value);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const act = (action: () => Promise<unknown>) => {
    setBusy(true);
    setActionError(null);
    action()
      .then(() => {
        setEditing(false);
        onChanged();
      })
      .catch((err: unknown) =>
        setActionError(err instanceof Error ? err.message : String(err)),
      )
      .finally(() => setBusy(false));
  };

  const save = () => {
    if (!draft.trim()) {
      setActionError('A memory needs a value; archive it instead.');
      return;
    }
    act(() => updateMemoryEntry(agentId, entry.key, { value: draft }));
  };

  const archive = () => {
    if (
      window.confirm(
        `Archive "${entry.key}"? It leaves search and ${agentId}'s context; its value is kept for audit.`,
      )
    ) {
      act(() => archiveMemoryEntry(agentId, entry.key));
    }
  };

  const purge = () => {
    if (
      window.confirm(
        `Erase "${entry.key}" and the earlier values in its history? This cannot be undone.`,
      )
    ) {
      act(() => purgeMemoryEntry(agentId, entry.key));
    }
  };

  return (
    <article
      className={
        'memory-page__entry' +
        (entry.pinned ? ' memory-page__entry--pinned' : '') +
        (entry.archived ? ' memory-page__entry--archived' : '')
      }
    >
      <div className="memory-page__entry-head">
        <span className="memory-page__entry-key">{entry.key}</span>
        {entry.pinned && (
          <span className="memory-page__entry-badge">pinned</span>
        )}
        {entry.archived && (
          <span className="memory-page__entry-badge memory-page__entry-badge--muted">
            archived
          </span>
        )}
        <span className="memory-page__entry-importance" title="stated importance">
          ★ {importance}
        </span>
        <span className="memory-page__entry-meta">
          {entry.access_count} reads · updated {formatRelativeTime(entry.updated_ms)}
        </span>
      </div>
      {editing ? (
        <div className="memory-page__entry-edit">
          <textarea
            className="memory-page__entry-editor"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={Math.min(12, Math.max(3, draft.split('\n').length + 1))}
            disabled={busy}
          />
          <div className="memory-page__entry-actions">
            <button type="button" onClick={save} disabled={busy}>
              {busy ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setDraft(entry.value);
                setActionError(null);
              }}
              disabled={busy}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <pre className="memory-page__entry-value">{entry.value}</pre>
      )}
      {!editing && !entry.archived && (
        <div className="memory-page__entry-actions">
          <button
            type="button"
            onClick={() => {
              setDraft(entry.value);
              setEditing(true);
            }}
            disabled={busy}
          >
            Edit
          </button>
          <button type="button" onClick={archive} disabled={busy}>
            Archive
          </button>
          {canPurge && (
            <button
              type="button"
              className="memory-page__entry-purge"
              onClick={purge}
              disabled={busy}
            >
              Purge
            </button>
          )}
        </div>
      )}
      {actionError && (
        <div className="memory-page__error">{actionError}</div>
      )}
      {entry.tags.length > 0 && (
        <div className="memory-page__entry-tags">
          {entry.tags.map((t) => (
            <span key={t} className="memory-page__entry-tag">
              {t}
            </span>
          ))}
        </div>
      )}
    </article>
  );
}
